import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Divider,
  Paper,
  alpha,
} from '@mui/material';
import { formatItemDisplayName } from '../utils/formatItemDisplayName';

const TEMPLATE_KEYS = [
  'standard',
  'thermal',
  'template3',
  'template4',
  'template5',
  'template6',
  'template7',
  'template8',
  'template9',
  'template10',
  'template11',
];

export function SalesInvoiceTemplateRenderer({
  order,
  company,
  templateKey,
  currency,
  itemFormat,
  dateFormat,
}) {
  const safeTemplateKey = TEMPLATE_KEYS.includes(templateKey) ? templateKey : 'standard';
  const safeCurrency = currency || company?.currency || 'USD';
  const safeItemFormat = itemFormat || company?.printItemNameFormat || 'name_sku';
  const safeDateFormat = dateFormat || company?.dateFormat || 'dd/MM/yyyy';

  const formatAmount = (value) => {
    const amount = Number(value || 0);
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: safeCurrency }).format(amount);
    } catch {
      return `${safeCurrency} ${amount.toFixed(2)}`;
    }
  };

  const formatItemName = (item) => {
    return formatItemDisplayName(
      { name: item.product?.name, sku: item.product?.sku, brand: item.product?.brand },
      safeItemFormat
    );
  };

  const formatDate = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    switch (safeDateFormat) {
      case 'MM/dd/yyyy':
        return `${mm}/${dd}/${yyyy}`;
      case 'yyyy-MM-dd':
        return `${yyyy}-${mm}-${dd}`;
      case 'dd-MM-yyyy':
        return `${dd}-${mm}-${yyyy}`;
      case 'dd/MM/yyyy':
      default:
        return `${dd}/${mm}/${yyyy}`;
    }
  };

  const invoiceDate = formatDate(order?.invoiceDate || order?.orderDate);
  const items = order?.items || [];

  const renderStandard = () => (
    <Box
      sx={{
        p: 4,
        maxWidth: 900,
        m: '0 auto',
        fontSize: 12,
        '@media print': {
          boxShadow: 'none',
          m: 0,
          p: 2,
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          {company?.logoUrl && (
            <Box
              component="img"
              src={company.logoUrl}
              alt=""
              sx={{ height: 48, width: 'auto', maxWidth: 120, objectFit: 'contain', display: 'block' }}
            />
          )}
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {company?.name || 'Company'}
            </Typography>
            {company?.address && (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {company.address}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Invoice
          </Typography>
          <Typography variant="body2">
            Invoice No: {order?.invoiceNumber || order?.orderNumber || '-'}
          </Typography>
          <Typography variant="body2">Date: {invoiceDate}</Typography>
          <Typography variant="body2">Sales Type: {order?.salesType || '-'}</Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ flex: 1, mr: 4 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Bill To
          </Typography>
          <Typography variant="body2">{order?.customer?.name || '-'}</Typography>
          {order?.customer?.address && <Typography variant="body2">{order.customer.address}</Typography>}
          {order?.customer?.trn && <Typography variant="body2">TRN: {order.customer.trn}</Typography>}
        </Box>
        <Box sx={{ flex: 1 }}>
          {order?.location && (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Branch / Store
              </Typography>
              <Typography variant="body2">
                {order.location.name} ({order.location.code})
              </Typography>
            </>
          )}
        </Box>
      </Box>

      <Table size="small" sx={{ mt: 2 }} aria-label="items">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Qty</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Unit</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Price</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Discount</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>VAT</TableCell>
            <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Line Total</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((it, idx) => (
            <TableRow key={it.id || idx}>
              <TableCell>{idx + 1}</TableCell>
              <TableCell>
                <Typography sx={{ fontSize: 12, fontWeight: 500 }}>{formatItemName(it)}</Typography>
                {it.batchNo && <Typography sx={{ fontSize: 11 }}>Batch: {it.batchNo}</Typography>}
                {it.expiryDate && (
                  <Typography sx={{ fontSize: 11 }}>
                    Exp: {new Date(it.expiryDate).toLocaleDateString()}
                  </Typography>
                )}
              </TableCell>
              <TableCell>{Number(it.quantity || 0).toFixed(2)}</TableCell>
              <TableCell>{it.unit || 'pcs'}</TableCell>
              <TableCell>{formatAmount(it.unitPrice)}</TableCell>
              <TableCell>{formatAmount(it.discount || 0)}</TableCell>
              <TableCell>{formatAmount(it.vatAmount || 0)}</TableCell>
              <TableCell sx={{ textAlign: 'right', fontWeight: 600 }}>{formatAmount(it.total || 0)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Box sx={{ width: 260 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2">Subtotal</Typography>
            <Typography variant="body2">{formatAmount(order?.subtotal)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2">Discount</Typography>
            <Typography variant="body2">{formatAmount(order?.discountTotal)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2">VAT</Typography>
            <Typography variant="body2">{formatAmount(order?.vatTotal)}</Typography>
          </Box>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Grand Total
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {formatAmount(order?.grandTotal || order?.totalAmount)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2">Paid</Typography>
            <Typography variant="body2">{formatAmount(order?.paidAmount)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2">Balance</Typography>
            <Typography variant="body2">{formatAmount(order?.balanceAmount)}</Typography>
          </Box>
        </Box>
      </Box>

      {order?.notes && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Notes
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
            {order.notes}
          </Typography>
        </Box>
      )}
    </Box>
  );

  const renderThermal = () => (
    <Box
      sx={{
        p: 2,
        maxWidth: 420,
        m: '0 auto',
        fontSize: 11,
        '@media print': {
          boxShadow: 'none',
          m: 0,
          p: 1,
          maxWidth: '100%',
        },
      }}
    >
      <Box sx={{ textAlign: 'center', mb: 1 }}>
        {company?.logoUrl && (
          <Box
            component="img"
            src={company.logoUrl}
            alt=""
            sx={{
              height: 36,
              width: 'auto',
              maxWidth: 100,
              objectFit: 'contain',
              mx: 'auto',
              mb: 0.5,
              display: 'block',
            }}
          />
        )}
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {company?.name || 'Company'}
        </Typography>
        {company?.address && (
          <Typography variant="caption" sx={{ whiteSpace: 'pre-line' }}>
            {company.address}
          </Typography>
        )}
        <Typography variant="caption" display="block">
          Invoice: {order?.invoiceNumber || order?.orderNumber || '-'}
        </Typography>
        <Typography variant="caption" display="block">
          Date: {invoiceDate}
        </Typography>
        <Typography variant="caption" display="block">
          Customer: {order?.customer?.name || '-'}
        </Typography>
      </Box>

      <Divider sx={{ my: 1 }} />

      <Table
        size="small"
        aria-label="items"
        sx={{ '& td, & th': { borderBottom: 'none', paddingY: 0.25 } }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, fontSize: 11 }}>Item</TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: 11 }} align="right">
              Qty
            </TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: 11 }} align="right">
              Price
            </TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: 11 }} align="right">
              Total
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((it, idx) => (
            <TableRow key={it.id || idx}>
              <TableCell sx={{ fontSize: 11 }}>
                {formatItemName(it)}
                {it.batchNo && (
                  <Typography variant="caption" display="block">
                    Batch: {it.batchNo}
                  </Typography>
                )}
              </TableCell>
              <TableCell align="right" sx={{ fontSize: 11 }}>
                {Number(it.quantity || 0).toFixed(2)}
              </TableCell>
              <TableCell align="right" sx={{ fontSize: 11 }}>
                {formatAmount(it.unitPrice)}
              </TableCell>
              <TableCell align="right" sx={{ fontSize: 11 }}>
                {formatAmount(it.total || 0)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Divider sx={{ my: 1 }} />

      <Box sx={{ mt: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption">Subtotal</Typography>
          <Typography variant="caption">{formatAmount(order?.subtotal)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption">Discount</Typography>
          <Typography variant="caption">{formatAmount(order?.discountTotal)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption">VAT</Typography>
          <Typography variant="caption">{formatAmount(order?.vatTotal)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            Grand Total
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            {formatAmount(order?.grandTotal || order?.totalAmount)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption">Paid</Typography>
          <Typography variant="caption">{formatAmount(order?.paidAmount)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption">Balance</Typography>
          <Typography variant="caption">{formatAmount(order?.balanceAmount)}</Typography>
        </Box>
      </Box>

      {order?.notes && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            Notes
          </Typography>
          <Typography variant="caption" display="block" sx={{ whiteSpace: 'pre-line' }}>
            {order.notes}
          </Typography>
        </Box>
      )}

      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <Typography variant="caption">Thank you for your business!</Typography>
      </Box>
    </Box>
  );

  const renderTemplate3 = () => (
    <Box
      sx={{
        p: 3,
        maxWidth: 820,
        m: '0 auto',
        fontSize: 11,
        '@media print': {
          boxShadow: 'none',
          m: 0,
          p: 2,
        },
      }}
    >
      <Box sx={{ textAlign: 'center', mb: 1 }}>
        {company?.logoUrl && (
          <Box
            component="img"
            src={company.logoUrl}
            alt=""
            sx={{
              height: 40,
              width: 'auto',
              maxWidth: 100,
              objectFit: 'contain',
              mx: 'auto',
              mb: 0.5,
              display: 'block',
            }}
          />
        )}
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {company?.name || 'Company'}
        </Typography>
        {company?.address && (
          <Typography variant="caption" sx={{ whiteSpace: 'pre-line' }}>
            {company.address}
          </Typography>
        )}
      </Box>
      <Divider sx={{ my: 1 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            Bill To
          </Typography>
          <Typography variant="caption" display="block">
            {order?.customer?.name || '-'}
          </Typography>
          {order?.customer?.address && (
            <Typography variant="caption" display="block">
              {order.customer.address}
            </Typography>
          )}
        </Box>
        <Box sx={{ flex: 1, textAlign: 'right' }}>
          <Typography variant="caption" display="block">
            Invoice: {order?.invoiceNumber || order?.orderNumber || '-'}
          </Typography>
          <Typography variant="caption" display="block">
            Date: {invoiceDate}
          </Typography>
          <Typography variant="caption" display="block">
            Sales Type: {order?.salesType || '-'}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            Template 3 – Minimal A4
          </Typography>
        </Box>
      </Box>
      <Divider sx={{ my: 1 }} />

      <Table size="small" aria-label="items" sx={{ '& th': { borderBottomWidth: 1 } }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Qty
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Price
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Total
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((it, idx) => (
            <TableRow key={it.id || idx}>
              <TableCell>{idx + 1}</TableCell>
              <TableCell>
                <Typography sx={{ fontSize: 11, fontWeight: 500 }}>{formatItemName(it)}</Typography>
              </TableCell>
              <TableCell align="right">{Number(it.quantity || 0).toFixed(2)}</TableCell>
              <TableCell align="right">{formatAmount(it.unitPrice)}</TableCell>
              <TableCell align="right">{formatAmount(it.total || 0)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Box sx={{ width: 240 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">Subtotal</Typography>
            <Typography variant="caption">{formatAmount(order?.subtotal)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">Discount</Typography>
            <Typography variant="caption">{formatAmount(order?.discountTotal)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">VAT</Typography>
            <Typography variant="caption">{formatAmount(order?.vatTotal)}</Typography>
          </Box>
          <Divider sx={{ my: 0.5 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              Grand Total
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              {formatAmount(order?.grandTotal || order?.totalAmount)}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  const renderTemplate4 = () => (
    <Box
      sx={{
        p: 3,
        maxWidth: 1000,
        m: '0 auto',
        fontSize: 11,
        '@media print': {
          boxShadow: 'none',
          m: 0,
          p: 2,
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flex: 1 }}>
          {company?.logoUrl && (
            <Box component="img" src={company.logoUrl} alt="" sx={{ height: 44, width: 'auto', maxWidth: 110, objectFit: 'contain' }} />
          )}
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {company?.name || 'Company'}
            </Typography>
            {company?.address && (
              <Typography variant="caption" sx={{ whiteSpace: 'pre-line' }}>
                {company.address}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ flex: 1, textAlign: 'right' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            TAX INVOICE
          </Typography>
          <Typography variant="caption" display="block">
            Invoice: {order?.invoiceNumber || order?.orderNumber || '-'}
          </Typography>
          <Typography variant="caption" display="block">
            Date: {invoiceDate}
          </Typography>
          <Typography variant="caption" display="block">
            Sales Type: {order?.salesType || '-'}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            Template 4 – Summary
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 1 }} />

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            Customer
          </Typography>
          <Typography variant="caption" display="block">
            {order?.customer?.name || '-'}
          </Typography>
          {order?.customer?.address && (
            <Typography variant="caption" display="block">
              {order.customer.address}
            </Typography>
          )}
          {order?.customer?.trn && (
            <Typography variant="caption" display="block">
              TRN: {order.customer.trn}
            </Typography>
          )}
        </Box>
        <Box sx={{ flex: 1 }}>
          {order?.location && (
            <>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                Branch / Store
              </Typography>
              <Typography variant="caption" display="block">
                {order.location.name} ({order.location.code})
              </Typography>
            </>
          )}
        </Box>
      </Box>

      <Table size="small" sx={{ mt: 1 }} aria-label="items">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Qty
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Price
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Disc.
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              VAT
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Line Total
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((it, idx) => (
            <TableRow key={it.id || idx}>
              <TableCell>
                <Typography sx={{ fontSize: 11, fontWeight: 500 }}>{formatItemName(it)}</Typography>
              </TableCell>
              <TableCell align="right">{Number(it.quantity || 0).toFixed(2)}</TableCell>
              <TableCell align="right">{formatAmount(it.unitPrice)}</TableCell>
              <TableCell align="right">{formatAmount(it.discount || 0)}</TableCell>
              <TableCell align="right">{formatAmount(it.vatAmount || 0)}</TableCell>
              <TableCell align="right">{formatAmount(it.total || 0)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
        <Box sx={{ flex: 1 }}>
          {order?.notes && (
            <>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                Notes
              </Typography>
              <Typography variant="caption" display="block" sx={{ whiteSpace: 'pre-line' }}>
                {order.notes}
              </Typography>
            </>
          )}
        </Box>
        <Box sx={{ width: 260 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">Subtotal</Typography>
            <Typography variant="caption">{formatAmount(order?.subtotal)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">Discount</Typography>
            <Typography variant="caption">{formatAmount(order?.discountTotal)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">VAT</Typography>
            <Typography variant="caption">{formatAmount(order?.vatTotal)}</Typography>
          </Box>
          <Divider sx={{ my: 0.5 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              Grand Total
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              {formatAmount(order?.grandTotal || order?.totalAmount)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">Paid</Typography>
            <Typography variant="caption">{formatAmount(order?.paidAmount)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">Balance</Typography>
            <Typography variant="caption">{formatAmount(order?.balanceAmount)}</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  const renderTemplate5 = () => (
    <Box
      sx={{
        p: 3,
        maxWidth: 900,
        m: '16px auto',
        fontSize: 12,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: '#FFFFFF',
        '@media print': {
          m: 0,
          borderRadius: 0,
          borderColor: '#CCCCCC',
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          {company?.logoUrl && (
            <Box component="img" src={company.logoUrl} alt="" sx={{ height: 48, width: 'auto', maxWidth: 120, objectFit: 'contain' }} />
          )}
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {company?.name || 'Company'}
            </Typography>
            {company?.address && (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {company.address}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Invoice
          </Typography>
          <Typography variant="body2">No: {order?.invoiceNumber || order?.orderNumber || '-'}</Typography>
          <Typography variant="body2">Date: {invoiceDate}</Typography>
          <Typography variant="body2">Sales Type: {order?.salesType || '-'}</Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 1.5 }} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Bill To
          </Typography>
          <Typography variant="body2">{order?.customer?.name || '-'}</Typography>
          {order?.customer?.address && <Typography variant="body2">{order.customer.address}</Typography>}
          {order?.customer?.trn && <Typography variant="body2">TRN: {order.customer.trn}</Typography>}
        </Box>
        <Box sx={{ flex: 1, textAlign: 'right' }}>
          {order?.location && (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Branch / Store
              </Typography>
              <Typography variant="body2">
                {order.location.name} ({order.location.code})
              </Typography>
            </>
          )}
        </Box>
      </Box>

      <Table
        size="small"
        aria-label="items"
        sx={{
          mt: 1,
          '& th, & td': {
            borderBottom: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        <TableHead>
          <TableRow sx={{ backgroundColor: 'grey.100' }}>
            <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Qty
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Unit
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Price
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Discount
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              VAT
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Line Total
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((it, idx) => (
            <TableRow key={it.id || idx}>
              <TableCell>{idx + 1}</TableCell>
              <TableCell>
                <Typography sx={{ fontSize: 12, fontWeight: 500 }}>{formatItemName(it)}</Typography>
              </TableCell>
              <TableCell align="right">{Number(it.quantity || 0).toFixed(2)}</TableCell>
              <TableCell align="right">{it.unit || 'pcs'}</TableCell>
              <TableCell align="right">{formatAmount(it.unitPrice)}</TableCell>
              <TableCell align="right">{formatAmount(it.discount || 0)}</TableCell>
              <TableCell align="right">{formatAmount(it.vatAmount || 0)}</TableCell>
              <TableCell align="right">{formatAmount(it.total || 0)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Box
          sx={{
            width: 260,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            p: 1,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2">Subtotal</Typography>
            <Typography variant="body2">{formatAmount(order?.subtotal)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2">Discount</Typography>
            <Typography variant="body2">{formatAmount(order?.discountTotal)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2">VAT</Typography>
            <Typography variant="body2">{formatAmount(order?.vatTotal)}</Typography>
          </Box>
          <Divider sx={{ my: 0.5 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Grand Total
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {formatAmount(order?.grandTotal || order?.totalAmount)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2">Paid</Typography>
            <Typography variant="body2">{formatAmount(order?.paidAmount)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2">Balance</Typography>
            <Typography variant="body2">{formatAmount(order?.balanceAmount)}</Typography>
          </Box>
        </Box>
      </Box>

      {order?.notes && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Notes
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
            {order.notes}
          </Typography>
        </Box>
      )}
    </Box>
  );

  const renderTemplate6 = () => (
    <Box
      sx={{
        p: 3,
        maxWidth: 920,
        m: '0 auto',
        fontSize: 12,
        border: '2px solid',
        borderColor: 'grey.300',
        borderRadius: 1,
        '@media print': { boxShadow: 'none', m: 0, borderColor: '#999' },
      }}
    >
      <Box sx={{ borderBottom: '2px solid', borderColor: 'grey.300', pb: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            {company?.logoUrl && (
              <Box component="img" src={company.logoUrl} alt="" sx={{ height: 48, width: 'auto', maxWidth: 120, objectFit: 'contain' }} />
            )}
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
                {company?.name || 'Company'}
              </Typography>
              {company?.address && (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line', mt: 0.5 }}>
                  {company.address}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              DETAILED INVOICE
            </Typography>
            <Typography variant="body2">No: {order?.invoiceNumber || order?.orderNumber || '-'}</Typography>
            <Typography variant="body2">Date: {invoiceDate}</Typography>
            <Typography variant="body2">Sales Type: {order?.salesType || '-'}</Typography>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          gap: 3,
          mb: 2,
          pb: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            Bill To
          </Typography>
          <Typography variant="body2">{order?.customer?.name || '-'}</Typography>
          {order?.customer?.address && <Typography variant="body2">{order.customer.address}</Typography>}
          {order?.customer?.trn && <Typography variant="body2">TRN: {order.customer.trn}</Typography>}
        </Box>
        {order?.location && (
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Branch / Store
            </Typography>
            <Typography variant="body2">
              {order.location.name} ({order.location.code})
            </Typography>
          </Box>
        )}
      </Box>
      <Table
        size="small"
        sx={{ '& th, & td': { border: '1px solid', borderColor: 'divider' } }}
        aria-label="items"
      >
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.200' }}>
            <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Item</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Qty</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Unit</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">
              Price
            </TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">
              Discount
            </TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">
              VAT
            </TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">
              Line Total
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((it, idx) => (
            <TableRow key={it.id || idx}>
              <TableCell>{idx + 1}</TableCell>
              <TableCell>
                <Typography sx={{ fontWeight: 500 }}>{formatItemName(it)}</Typography>
                {it.batchNo && <Typography variant="caption">Batch: {it.batchNo}</Typography>}
                {it.expiryDate && (
                  <Typography variant="caption" display="block">
                    Exp: {new Date(it.expiryDate).toLocaleDateString()}
                  </Typography>
                )}
              </TableCell>
              <TableCell>{Number(it.quantity || 0).toFixed(2)}</TableCell>
              <TableCell>{it.unit || 'pcs'}</TableCell>
              <TableCell align="right">{formatAmount(it.unitPrice)}</TableCell>
              <TableCell align="right">{formatAmount(it.discount || 0)}</TableCell>
              <TableCell align="right">{formatAmount(it.vatAmount || 0)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                {formatAmount(it.total || 0)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Box sx={{ width: 280, border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2">Subtotal</Typography>
            <Typography variant="body2">{formatAmount(order?.subtotal)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2">Discount</Typography>
            <Typography variant="body2">{formatAmount(order?.discountTotal)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2">VAT</Typography>
            <Typography variant="body2">{formatAmount(order?.vatTotal)}</Typography>
          </Box>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Grand Total
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {formatAmount(order?.grandTotal || order?.totalAmount)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
            <Typography variant="body2">Paid</Typography>
            <Typography variant="body2">{formatAmount(order?.paidAmount)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2">Balance</Typography>
            <Typography variant="body2">{formatAmount(order?.balanceAmount)}</Typography>
          </Box>
        </Box>
      </Box>
      {order?.notes && (
        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Notes
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
            {order.notes}
          </Typography>
        </Box>
      )}
    </Box>
  );

  const renderTemplate7 = () => (
    <Box sx={{ p: 2, maxWidth: 800, m: '0 auto', fontSize: 11, '@media print': { m: 0, p: 1 } }}>
      <Box
        sx={{
          textAlign: 'center',
          borderBottom: '3px solid',
          borderColor: 'primary.main',
          pb: 1.5,
          mb: 1.5,
        }}
      >
        {company?.logoUrl && (
          <Box component="img" src={company.logoUrl} alt="" sx={{ height: 36, width: 'auto', maxWidth: 100, objectFit: 'contain', mx: 'auto', mb: 0.5, display: 'block' }} />
        )}
        <Typography variant="h5" sx={{ fontWeight: 800 }}>
          {company?.name || 'Company'}
        </Typography>
        {company?.address && (
          <Typography variant="caption" sx={{ whiteSpace: 'pre-line' }}>
            {company.address}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            Customer:
          </Typography>
          <Typography variant="body2">{order?.customer?.name || '-'}</Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="body2">Invoice #{order?.invoiceNumber || order?.orderNumber || '-'}</Typography>
          <Typography variant="body2">Date: {invoiceDate}</Typography>
        </Box>
      </Box>
      <Table size="small" aria-label="items" sx={{ '& td, & th': { py: 0.4 } }}>
        <TableHead>
          <TableRow sx={{ borderBottom: '2px solid', borderColor: 'grey.400' }}>
            <TableCell sx={{ fontWeight: 700 }}>Item</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">
              Qty
            </TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">
              Price
            </TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">
              Total
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((it, idx) => (
            <TableRow key={it.id || idx}>
              <TableCell>
                <Typography sx={{ fontSize: 11 }}>{formatItemName(it)}</Typography>
              </TableCell>
              <TableCell align="right">{Number(it.quantity || 0).toFixed(2)}</TableCell>
              <TableCell align="right">{formatAmount(it.unitPrice)}</TableCell>
              <TableCell align="right">{formatAmount(it.total || 0)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'grey.100', borderRadius: 1, maxWidth: 320, ml: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2">Subtotal</Typography>
          <Typography variant="body2">{formatAmount(order?.subtotal)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2">Discount</Typography>
          <Typography variant="body2">{formatAmount(order?.discountTotal)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2">VAT</Typography>
          <Typography variant="body2">{formatAmount(order?.vatTotal)}</Typography>
        </Box>
        <Divider sx={{ my: 0.5 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" sx={{ fontWeight: 800, fontSize: 13 }}>
            TOTAL
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 800, fontSize: 13 }}>
            {formatAmount(order?.grandTotal || order?.totalAmount)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
          <Typography variant="caption">Paid</Typography>
          <Typography variant="caption">{formatAmount(order?.paidAmount)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption">Balance</Typography>
          <Typography variant="caption">{formatAmount(order?.balanceAmount)}</Typography>
        </Box>
      </Box>
      {order?.notes && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            Notes:
          </Typography>
          <Typography variant="caption" display="block" sx={{ whiteSpace: 'pre-line' }}>
            {order.notes}
          </Typography>
        </Box>
      )}
    </Box>
  );

  const renderTemplate8 = () => (
    <Box sx={{ p: 3, maxWidth: 700, m: '0 auto', fontSize: 12, '@media print': { m: 0, p: 2 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          {company?.logoUrl && (
            <Box component="img" src={company.logoUrl} alt="" sx={{ height: 44, width: 'auto', maxWidth: 110, objectFit: 'contain' }} />
          )}
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {company?.name || 'Company'}
            </Typography>
            {company?.address && (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {company.address}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="overline" sx={{ fontWeight: 700 }}>
            SERVICE INVOICE
          </Typography>
          <Typography variant="body2" display="block">
            No: {order?.invoiceNumber || order?.orderNumber || '-'}
          </Typography>
          <Typography variant="body2" display="block">
            Date: {invoiceDate}
          </Typography>
        </Box>
      </Box>
      <Divider sx={{ my: 2 }} />
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
          Bill To
        </Typography>
        <Typography variant="body2">{order?.customer?.name || '-'}</Typography>
        {order?.customer?.address && <Typography variant="body2">{order.customer.address}</Typography>}
      </Box>
      <Table size="small" aria-label="items">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Qty
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Rate
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Amount
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((it, idx) => (
            <TableRow key={it.id || idx}>
              <TableCell>
                <Typography sx={{ fontWeight: 500 }}>{formatItemName(it)}</Typography>
              </TableCell>
              <TableCell align="right">{Number(it.quantity || 0).toFixed(2)}</TableCell>
              <TableCell align="right">{formatAmount(it.unitPrice)}</TableCell>
              <TableCell align="right">{formatAmount(it.total || 0)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2">Subtotal</Typography>
          <Typography variant="body2">{formatAmount(order?.subtotal)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2">Discount</Typography>
          <Typography variant="body2">{formatAmount(order?.discountTotal)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2">VAT</Typography>
          <Typography variant="body2">{formatAmount(order?.vatTotal)}</Typography>
        </Box>
        <Divider sx={{ my: 1 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Amount Due
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {formatAmount(order?.grandTotal || order?.totalAmount)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
          <Typography variant="caption">Paid</Typography>
          <Typography variant="caption">{formatAmount(order?.paidAmount)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption">Balance</Typography>
          <Typography variant="caption">{formatAmount(order?.balanceAmount)}</Typography>
        </Box>
      </Box>
      {order?.notes && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Notes
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
            {order.notes}
          </Typography>
        </Box>
      )}
    </Box>
  );

  const renderTemplate9 = () => (
    <Box sx={{ p: 2, maxWidth: 1100, m: '0 auto', fontSize: 11, '@media print': { m: 0 } }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3, mb: 2, alignItems: 'start' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          {company?.logoUrl && (
            <Box component="img" src={company.logoUrl} alt="" sx={{ height: 44, width: 'auto', maxWidth: 110, objectFit: 'contain', flexShrink: 0 }} />
          )}
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {company?.name || 'Company'}
            </Typography>
            {company?.address && (
              <Typography variant="caption" sx={{ whiteSpace: 'pre-line' }}>
                {company.address}
              </Typography>
            )}
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                Bill To
              </Typography>
              <Typography variant="body2" display="block">
                {order?.customer?.name || '-'}
              </Typography>
              {order?.customer?.address && (
                <Typography variant="caption" display="block">
                  {order.customer.address}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            INVOICE
          </Typography>
          <Typography variant="body2">No: {order?.invoiceNumber || order?.orderNumber || '-'}</Typography>
          <Typography variant="body2">Date: {invoiceDate}</Typography>
          <Typography variant="body2">Sales Type: {order?.salesType || '-'}</Typography>
          {order?.location && (
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              {order.location.name} ({order.location.code})
            </Typography>
          )}
        </Box>
      </Box>
      <Divider sx={{ my: 1 }} />
      <Table size="small" aria-label="items" sx={{ tableLayout: 'fixed' }}>
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.100' }}>
            <TableCell sx={{ fontWeight: 600, width: '5%' }}>#</TableCell>
            <TableCell sx={{ fontWeight: 600, width: '35%' }}>Item</TableCell>
            <TableCell sx={{ fontWeight: 600, width: '12%' }} align="right">
              Qty
            </TableCell>
            <TableCell sx={{ fontWeight: 600, width: '14%' }} align="right">
              Unit Price
            </TableCell>
            <TableCell sx={{ fontWeight: 600, width: '14%' }} align="right">
              Disc.
            </TableCell>
            <TableCell sx={{ fontWeight: 600, width: '14%' }} align="right">
              VAT
            </TableCell>
            <TableCell sx={{ fontWeight: 600, width: '16%' }} align="right">
              Total
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((it, idx) => (
            <TableRow key={it.id || idx}>
              <TableCell>{idx + 1}</TableCell>
              <TableCell>
                <Typography sx={{ fontSize: 11 }}>{formatItemName(it)}</Typography>
              </TableCell>
              <TableCell align="right">{Number(it.quantity || 0).toFixed(2)}</TableCell>
              <TableCell align="right">{formatAmount(it.unitPrice)}</TableCell>
              <TableCell align="right">{formatAmount(it.discount || 0)}</TableCell>
              <TableCell align="right">{formatAmount(it.vatAmount || 0)}</TableCell>
              <TableCell align="right">{formatAmount(it.total || 0)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Box sx={{ width: 280 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">Subtotal</Typography>
            <Typography variant="caption">{formatAmount(order?.subtotal)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">Discount</Typography>
            <Typography variant="caption">{formatAmount(order?.discountTotal)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">VAT</Typography>
            <Typography variant="caption">{formatAmount(order?.vatTotal)}</Typography>
          </Box>
          <Divider sx={{ my: 0.5 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Grand Total
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {formatAmount(order?.grandTotal || order?.totalAmount)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
            <Typography variant="caption">Paid</Typography>
            <Typography variant="caption">{formatAmount(order?.paidAmount)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">Balance</Typography>
            <Typography variant="caption">{formatAmount(order?.balanceAmount)}</Typography>
          </Box>
        </Box>
      </Box>
      {order?.notes && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            Notes
          </Typography>
          <Typography variant="caption" display="block" sx={{ whiteSpace: 'pre-line' }}>
            {order.notes}
          </Typography>
        </Box>
      )}
    </Box>
  );

  const renderTemplate10 = () => (
    <Box sx={{ p: 3, maxWidth: 880, m: '0 auto', fontSize: 12, '@media print': { m: 0 } }}>
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 2,
          border: '1px solid',
          borderColor: 'primary.light',
          borderRadius: 2,
          bgcolor: alpha('#1976d2', 0.04),
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            {company?.logoUrl && (
              <Box component="img" src={company.logoUrl} alt="" sx={{ height: 44, width: 'auto', maxWidth: 110, objectFit: 'contain' }} />
            )}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.dark' }}>
                {company?.name || 'Company'}
              </Typography>
              {company?.address && (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                  {company.address}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.dark' }}>
              Invoice
            </Typography>
            <Typography variant="body2">No: {order?.invoiceNumber || order?.orderNumber || '-'}</Typography>
            <Typography variant="body2">Date: {invoiceDate}</Typography>
          </Box>
        </Box>
      </Paper>
      <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Customer
        </Typography>
        <Typography variant="body2">{order?.customer?.name || '-'}</Typography>
        {order?.customer?.address && <Typography variant="body2">{order.customer.address}</Typography>}
      </Paper>
      <Table
        size="small"
        component={Paper}
        elevation={0}
        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}
        aria-label="items"
      >
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.100' }}>
            <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Qty
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Price
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Total
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((it, idx) => (
            <TableRow key={it.id || idx}>
              <TableCell>{idx + 1}</TableCell>
              <TableCell>{formatItemName(it)}</TableCell>
              <TableCell align="right">{Number(it.quantity || 0).toFixed(2)}</TableCell>
              <TableCell align="right">{formatAmount(it.unitPrice)}</TableCell>
              <TableCell align="right">{formatAmount(it.total || 0)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Paper
        elevation={0}
        sx={{
          mt: 2,
          p: 2,
          border: '1px solid',
          borderColor: 'primary.light',
          borderRadius: 2,
          maxWidth: 300,
          ml: 'auto',
          bgcolor: alpha('#1976d2', 0.04),
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2">Subtotal</Typography>
          <Typography variant="body2">{formatAmount(order?.subtotal)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2">Discount</Typography>
          <Typography variant="body2">{formatAmount(order?.discountTotal)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2">VAT</Typography>
          <Typography variant="body2">{formatAmount(order?.vatTotal)}</Typography>
        </Box>
        <Divider sx={{ my: 1 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            Grand Total
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {formatAmount(order?.grandTotal || order?.totalAmount)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
          <Typography variant="caption">Paid</Typography>
          <Typography variant="caption">{formatAmount(order?.paidAmount)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption">Balance</Typography>
          <Typography variant="caption">{formatAmount(order?.balanceAmount)}</Typography>
        </Box>
      </Paper>
      {order?.notes && (
        <Paper elevation={0} sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Notes
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
            {order.notes}
          </Typography>
        </Paper>
      )}
    </Box>
  );

  // ── Template 11 – Image-based style (Tax invoice grid + signatures) ─────
  const renderTemplate11 = () => {
    const taxLabel = company?.taxLabel || 'VAT';
    const vatRateLabel = (() => {
      const r = company?.vatRate;
      if (r == null || r === '') return `${taxLabel}`;
      const n = Number(r);
      return Number.isFinite(n) ? `${taxLabel} ${n}%` : `${taxLabel}`;
    })();

    const totalAfterVat = Number(order?.grandTotal || order?.totalAmount || 0);
    const vatTotal = Number(order?.vatTotal || 0);
    const totalBeforeVat = totalAfterVat - vatTotal;

    const toWords = (amount) => {
      // Simple English words (good enough for preview & printing; can be upgraded later)
      const ones = [
        '',
        'One',
        'Two',
        'Three',
        'Four',
        'Five',
        'Six',
        'Seven',
        'Eight',
        'Nine',
        'Ten',
        'Eleven',
        'Twelve',
        'Thirteen',
        'Fourteen',
        'Fifteen',
        'Sixteen',
        'Seventeen',
        'Eighteen',
        'Nineteen',
      ];
      const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
      const n = Math.floor(Math.abs(Number(amount || 0)));
      const toWordsInt = (x) => {
        if (x === 0) return '';
        if (x < 20) return ones[x];
        if (x < 100) return tens[Math.floor(x / 10)] + (x % 10 ? ` ${ones[x % 10]}` : '');
        if (x < 1000)
          return (
            ones[Math.floor(x / 100)] +
            ' Hundred' +
            (x % 100 ? ` ${toWordsInt(x % 100)}` : '')
          );
        if (x < 1000000)
          return (
            `${toWordsInt(Math.floor(x / 1000))} Thousand` + (x % 1000 ? ` ${toWordsInt(x % 1000)}` : '')
          );
        return String(amount);
      };
      const whole = toWordsInt(n).trim() || 'Zero';
      return whole;
    };

    return (
      <Box
        sx={{
          p: 2.5,
          maxWidth: 900,
          m: '0 auto',
          fontSize: 11,
          border: '1px solid',
          borderColor: 'grey.400',
          position: 'relative',
          overflow: 'hidden',
          '@media print': { m: 0, p: 1.5, borderColor: 'grey.500' },
        }}
      >
        {/* Watermark */}
        {company?.logoUrl && (
          <Box
            aria-hidden="true"
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
              zIndex: 0,
              '@media print': {
                WebkitPrintColorAdjust: 'exact',
                printColorAdjust: 'exact',
              },
            }}
          >
            <Box
              component="img"
              src={company.logoUrl}
              alt=""
              sx={{
                width: '74%',
                maxWidth: 680,
                opacity: 0.08,
                filter: 'grayscale(100%)',
                transform: 'rotate(-12deg)',
              }}
            />
          </Box>
        )}

        <Box sx={{ position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <Box
          sx={{
            pb: 1,
            borderBottom: '2px solid',
            borderColor: 'grey.400',
            display: 'grid',
            gridTemplateColumns: company?.logoUrl ? '132px 1fr 132px' : '1fr',
            alignItems: 'center',
            columnGap: 1,
          }}
        >
          {company?.logoUrl && (
            <Box
              component="img"
              src={company.logoUrl}
              alt=""
              sx={{ width: 120, height: 120, objectFit: 'contain', justifySelf: 'start' }}
            />
          )}

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
              {company?.name || 'Company'}
            </Typography>
            <Typography variant="subtitle2" sx={{ mt: 0.5, fontWeight: 700 }}>
              TAX INVOICE
            </Typography>
            {(company?.vatNumber || company?.taxId) && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                TRN: {company?.vatNumber || company?.taxId}
              </Typography>
            )}
          </Box>

          {company?.logoUrl && <Box sx={{ width: 120, height: 120 }} />}
        </Box>

        {/* Info grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 0,
            borderBottom: '1px solid',
            borderColor: 'grey.400',
          }}
        >
          <Box sx={{ p: 1.25, borderRight: '1px solid', borderColor: 'grey.400' }}>
            {[
              ['Name', order?.customer?.name],
              ['Address', order?.customer?.address],
              ['Tel No', order?.customer?.phone],
              ['Sales Rep', order?.salesRep],
              ['Cust TRN', order?.customer?.trn],
            ]
              .filter(([, v]) => v)
              .map(([k, v]) => (
                <Box key={k} sx={{ display: 'grid', gridTemplateColumns: '88px 1fr', columnGap: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    {k}
                  </Typography>
                  <Typography variant="caption" sx={{ whiteSpace: 'pre-line' }}>
                    {String(v)}
                  </Typography>
                </Box>
              ))}
          </Box>
          <Box sx={{ p: 1.25 }}>
            {[
              ['Invoice No', order?.invoiceNumber || order?.orderNumber],
              ['Invoice Date', invoiceDate],
              ['LPO No', order?.lpoNo],
              ['LPO Date', order?.lpoDate ? formatDate(order.lpoDate) : undefined],
              ['DO No', order?.doNo],
              ['Job No', order?.jobNo],
            ]
              .filter(([, v]) => v)
              .map(([k, v]) => (
                <Box key={k} sx={{ display: 'grid', gridTemplateColumns: '88px 1fr', columnGap: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    {k}
                  </Typography>
                  <Typography variant="caption">{String(v)}</Typography>
                </Box>
              ))}
          </Box>
        </Box>

        {/* Items table */}
        <Table
          size="small"
          sx={{
            mt: 0,
            '& th, & td': {
              borderBottom: '1px solid',
              borderColor: 'grey.300',
              fontSize: 11,
              py: 0.6,
            },
            '& thead th': {
              borderBottom: '2px solid',
              borderColor: 'grey.400',
              fontWeight: 800,
              bgcolor: alpha('#000', 0.03),
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 52 }}>SNo</TableCell>
              <TableCell>Description</TableCell>
              <TableCell sx={{ width: 70 }} align="right">
                Qty
              </TableCell>
              <TableCell sx={{ width: 90 }} align="right">
                Rate
              </TableCell>
              <TableCell sx={{ width: 90 }} align="right">
                Amount
              </TableCell>
              <TableCell sx={{ width: 70 }} align="right">
                Vat Rate
              </TableCell>
              <TableCell sx={{ width: 80 }} align="right">
                Vat Amt
              </TableCell>
              <TableCell sx={{ width: 110 }} align="right">
                Total Amount
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((it, idx) => {
              const qty = Number(it.quantity || 0);
              const rate = Number(it.unitPrice || 0);
              const amount = qty * rate - Number(it.discount || 0);
              const vatAmt = Number(it.vatAmount || 0);
              const total = Number(it.total || amount + vatAmt);
              const vatRate = (() => {
                const r = company?.vatRate;
                const n = Number(r);
                return Number.isFinite(n) ? `${n}%` : '-';
              })();
              return (
                <TableRow key={it.id || idx}>
                  <TableCell>{idx + 1}</TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: 11, fontWeight: 600 }}>{formatItemName(it)}</Typography>
                    {it.description && (
                      <Typography variant="caption" sx={{ whiteSpace: 'pre-line', color: 'text.secondary' }}>
                        {it.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">{qty.toFixed(2)}</TableCell>
                  <TableCell align="right">{formatAmount(rate)}</TableCell>
                  <TableCell align="right">{formatAmount(amount)}</TableCell>
                  <TableCell align="right">{vatRate}</TableCell>
                  <TableCell align="right">{formatAmount(vatAmt)}</TableCell>
                  <TableCell align="right">{formatAmount(total)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        {/* Amount in words + totals */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 260px',
            gap: 0,
            borderTop: '1px solid',
            borderColor: 'grey.400',
            mt: 0,
          }}
        >
          <Box sx={{ p: 1.25, borderRight: '1px solid', borderColor: 'grey.400' }}>
            <Typography variant="caption" sx={{ fontWeight: 800 }}>
              {safeCurrency}:
            </Typography>{' '}
            <Typography variant="caption">
              {toWords(totalAfterVat)} Only
            </Typography>
          </Box>
          <Box sx={{ p: 0 }}>
            <Box sx={{ borderBottom: '1px solid', borderColor: 'grey.300', p: 1, display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" sx={{ fontWeight: 800 }}>
                Total Before {taxLabel}
              </Typography>
              <Typography variant="caption">{formatAmount(totalBeforeVat)}</Typography>
            </Box>
            <Box sx={{ borderBottom: '1px solid', borderColor: 'grey.300', p: 1, display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" sx={{ fontWeight: 800 }}>
                {vatRateLabel}
              </Typography>
              <Typography variant="caption">{formatAmount(order?.vatTotal)}</Typography>
            </Box>
            <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" sx={{ fontWeight: 900 }}>
                Total After {taxLabel}
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 900 }}>
                {formatAmount(totalAfterVat)}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Notes + signatures */}
        <Box sx={{ borderTop: '1px solid', borderColor: 'grey.400', mt: 0, p: 1.25 }}>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            Cheque should be issued in favour of {company?.name || 'Company'}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            borderTop: '1px solid',
            borderColor: 'grey.400',
          }}
        >
          <Box sx={{ p: 1.25, borderRight: '1px solid', borderColor: 'grey.400' }}>
            {['Receiver’s Name', 'Receiver’s Mobile', 'Receiver’s Signature'].map((label) => (
              <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="caption" sx={{ width: 130, fontWeight: 700 }}>
                  {label}:
                </Typography>
                <Box sx={{ flex: 1, borderBottom: '1px solid', borderColor: 'grey.500', height: 14 }} />
              </Box>
            ))}
          </Box>
          <Box sx={{ p: 1.25 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                For
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 800 }}>
                {company?.name || 'Company'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Box sx={{ width: 220, textAlign: 'center' }}>
                <Box sx={{ height: 28, borderBottom: '1px solid', borderColor: 'grey.500' }} />
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                  Authorised Signature
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Footer company details */}
        <Box
          sx={{
            mt: 0,
            pt: 1,
            borderTop: '2px solid',
            borderColor: 'grey.400',
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: 0.25,
          }}
        >
          <Typography variant="caption" sx={{ textAlign: 'center', fontWeight: 700 }}>
            {(company?.phone || company?.email) && (
              <>
                {company?.phone ? `Tel: ${company.phone}` : ''}
                {company?.phone && company?.email ? '  |  ' : ''}
                {company?.email ? `Email: ${company.email}` : ''}
              </>
            )}
          </Typography>
          {company?.address && (
            <Typography variant="caption" sx={{ textAlign: 'center', color: 'text.secondary', whiteSpace: 'pre-line' }}>
              {company.address}
            </Typography>
          )}
          {(company?.vatNumber || company?.taxId) && (
            <Typography variant="caption" sx={{ textAlign: 'center', color: 'text.secondary' }}>
              TRN: {company?.vatNumber || company?.taxId}
            </Typography>
          )}
        </Box>
        </Box>
      </Box>
    );
  };

  if (safeTemplateKey === 'thermal') return renderThermal();
  if (safeTemplateKey === 'template3') return renderTemplate3();
  if (safeTemplateKey === 'template4') return renderTemplate4();
  if (safeTemplateKey === 'template5') return renderTemplate5();
  if (safeTemplateKey === 'template6') return renderTemplate6();
  if (safeTemplateKey === 'template7') return renderTemplate7();
  if (safeTemplateKey === 'template8') return renderTemplate8();
  if (safeTemplateKey === 'template9') return renderTemplate9();
  if (safeTemplateKey === 'template10') return renderTemplate10();
  if (safeTemplateKey === 'template11') return renderTemplate11();
  return renderStandard();
}

